import type { IValidationErrorDto, IValidatorResult } from "@/types/FormEngine/validation";
import { defineComponent } from "vue";
import { tableStateStore } from "@/library/stores/TableStateStore";
import type { INavigationStep } from "@/types/ComponentLibrary/component-library";
import VRuntimeTemplate from "vue3-runtime-template";
import { t } from "@/services/Translation/t";
import { XmlHelper } from "@/helpers/Xml";

export default defineComponent({
  name: "Table",
  components: {
    VRuntimeTemplate,
  },
  props: {
    identifier: { type: String },
    mtdt: {
      type: Object as IField | any,
    },
    level: {
      type: Number,
    },
  },
  data() {
    let store = tableStateStore(this.identifier as string);
    let parent = window.FormEngine.form.sections.get(this.mtdt.Parent);
    let tableControl = null;
    if (this.mtdt.IsTableDetailElement) {
      tableControl = window.FormEngine.form.controls.get(this.mtdt.ParentTableControl);
    }
    if (!parent) {
      console.log("DOESN't HAVE A PARENT => ", this.identifier);
    }
    if (store.reload) {
      let normalizedXpath = "";
      let xmlNodeSelector = "";
      let xmlLastNodeSelector = "";

      if (this.mtdt.XpathEdoc && this.mtdt.XmlNodeSelector) {
        normalizedXpath = this.mtdt.XpathEdoc;
        xmlNodeSelector = this.mtdt.XmlNodeSelector;
        xmlLastNodeSelector = xmlNodeSelector.split(" > ").pop() as string;
      }

      let valuesToControlsMapping: Dictionary<String> = this.mtdt.ValuesToControlsMapping
        ? this.mtdt.ValuesToControlsMapping
        : ({} as Dictionary<String>);
      let valuesToColumnsMapping: Dictionary<String> = this.mtdt.ValuesToColumnsMapping
        ? this.mtdt.ValuesToColumnsMapping
        : ({} as Dictionary<String>);

      store.state = {
        isValid: true,
        errorMessages: new Array<IValidationErrorDto>(),
        hidden: this.mtdt.IsHidden === null ? false : this.mtdt.IsHidden,
        readOnly: this.mtdt.ReadOnlyFlag,
        hiddenLabel: this.mtdt.HasHiddenLabel === null ? false : (this.mtdt.HasHiddenLabel as boolean),
        hiddenHint: true,
        customMask: this.mtdt.CustomMask,
        value: this.mtdt.DefaultValue ? JSON.parse(JSON.stringify(this.mtdt.DefaultValue)) : [],
        silentSetValue: false,
        previousValue: null,
        displayValue: "",
        required: this.mtdt.IsRequired === null ? false : this.mtdt.IsRequired,
        description: this.mtdt.Description,
        masked: this.mtdt.IsMasked,
        parent: parent as any,
        parentTableControl: tableControl ? tableControl : null,
        classes: "",
        toExport: xmlNodeSelector ? true : false,
        detailViewOpened: false,
        editedRowNumber: 0 as number | undefined,
        editedRow: null,
        rows: new Array<ITableRow>(),
        hasHiddenCaption: this.mtdt.HasHiddenCaption,
      } as ITableState;
      if (this.mtdt.CustomMethods) {
        store.state.customMethods = this.mtdt.CustomMethods;
      }
      store.config = {
        valuesToControlsMapping: valuesToControlsMapping,
        valuesToColumnsMapping: valuesToColumnsMapping,
        normalizedXpath: normalizedXpath,
        xmlNodeSelector: xmlNodeSelector,
        xmlLastNodeSelector: xmlLastNodeSelector,
        displayValueFormatting: new Map<String, Function>(),
        checkBoxColumn: this.mtdt.CheckboxColumn,
        columns: this.mtdt.Columns,
        editedParentRowNumber: null,
        detail: this.mtdt.Detail ? this.mtdt.Detail : null,
      } as ITableConfig;
    }

    store.reload = false;
    return {
      store: store,
      htmlElement: null as any,
      inputElement: null as any,
      defaultState: {
        isValid: true,
        hidden: this.mtdt.IsHidden,
        readOnly: this.mtdt.ReadOnlyFlag,
        hiddenLabel: !this.mtdt.HasHiddenLabel,
        customMask: this.mtdt.CustomMask,
        value: this.mtdt.DefaultValue,
        displayValue: "",
        required: this.mtdt.IsRequired,
        description: this.mtdt.Description,
        masked: this.mtdt.IsMasked,
        classes: "",
      },
      state: store.state,
      config: store.config,
    };
  },
  created() {
    window.FormEngine.form.registerControl(this.identifier, this);
  },
  mounted() {
    if (this.identifier) {
      //console.log(this.identifier, " - Is mounted");
      this.htmlElement = document.getElementById(this.identifier);
      this.inputElement = document.getElementById(this.controlId);
    }
    if (this.state.customMethods) {
      // console.log(this.identifier, this.state.customMethods);
      for (let methodKey in this.state.customMethods) {
        this[methodKey as string] = this.state.customMethods[methodKey];
      }
    }
    if (this.state.readOnly) {
      return;
    }
    this.mapDetailControlsRecursive();
  },
  computed: {
    fillErrorBagComputed(): boolean {
      let fillErrorBag = true;
      if (
        this.mtdt.IsTableDetailElement &&
        this.state.parentTableControl &&
        this.state.parentTableControl.state.detailViewOpened &&
        this.config.editedParentRowNumber === null
      ) {
        fillErrorBag = false;
      }
      return fillErrorBag;
    },
    currentLevel() {
      return this.level;
    },
    errorId() {
      return this.identifier + "-error";
    },
    currentErrors() {
      return this.state.errorMessages;
    },
    noRowSelected() {
      return this.state.rows.find((row) => row.checked) === undefined;
    },
    allRowsSelected: {
      get() {
        return this.state.rows.length > 0 && this.state.rows.find((row) => !row.checked) === undefined;
      },
      set(value: boolean) {
        this.state.rows.map((item) => (item.checked = value));
      },
    },
    oneRowSelected() {
      let array = this.state.rows.filter((row) => row.checked);
      return array && array.length === 1;
    },
    multipleRowsSelected() {
      let array = this.state.rows.filter((row) => row.checked);
      return array && array.length > 1;
    },
    detailMtdt() {
      if (this.config.detail) {
        return Object.assign(this.config.detail, {
          HasHiddenCaption: true,
          HasHiddenLabel: false,
          Parent: this.identifier,
          isOpen: true,
          IsHidden: false,
          Classes: "idsk-table-detail-section",
        });
      }
      return {};
    },
    detailLevel() {
      return (this.level as number) + 1;
    },
    controlId() {
      return this.identifier + "-control";
    },
    isVisible() {
      var style = window.getComputedStyle(this.htmlElement);
      return style.display === "none";
      //return !(this.htmlElement.offsetParent === null);
    },
    displayHint(): string {
      return t("Hint", this.identifier);
    },
    displayHintText(): string {
      return this.state.hiddenHint ? t("displayHintTextClosed", "_utility") : t("displayHintTextOpened", "_utility");
    },
    displayDetailHintText(): string {
      return this.config.detail?.Hint ? t("Hint", this.config.detail.Identifier) : "";
    },
    displayHintClasses(): string {
      return "govuk-display-hint " + (this.state.hiddenHint ? "hint-closed" : "hint-opened");
    },
    hintId() {
      return this.identifier + "-hint";
    },
    displayTitle(): string {
      return t("Title", this.identifier);
    },
    titleId() {
      return this.identifier + "-title";
    },
  },
  watch: {
    "state.value": {
      handler(newValue, oldValue) {
        this.state.previousValue = oldValue;
        if (!this.state.silentValueChangeEvent) {
          window.FormEngine.events.publish(
            window.FormEngine.events.list.VALUE_CHANGE,
            {
              validableId: this.identifier,
              control: this,
              from: oldValue,
              to: newValue,
            },
            this.identifier
          );
        } else {
          this.state.silentValueChangeEvent = false;
        }
        if (!this.state.silentSetValue) {
          //console.log("Before Table value watch validate ", this.identifier);
          this.validate();
        } else {
          this.state.silentSetValue = false;
        }
      },
      deep: true,
    },
  },
  methods: {
    t, // Translation
    // Event system helper functions
    subscribe(event: string, callback: Function, specifier?: string) {
      window.FormEngine.events.subscribe(event, callback, this.identifier, specifier, null);
    },
    toggleHintSection() {
      this.state.hiddenHint = !this.state.hiddenHint;
    },
    /**
     * Validation
     */
    validate(fillErrorBag: boolean = true, fillControlErrors: boolean = true) {
      // if(!this.mtdt.IsTableDetailElement)
      // window.FormEngine.form.errorBag.removeErrors(this.identifier);
      this.resetErrors();
      let validationDto = window.FormEngine.form.validation.newValidatorDto({}, this);
      if (this.mtdt.IsTableDetailElement && this.state.parentTableControl && this.state.parentTableControl.state.detailViewOpened) {
        fillErrorBag = false;
      }
      //console.log("Validate function in Table => ", this.identifier, fillErrorBag, fillControlErrors, validationDto);

      let results = window.FormEngine.events.publish(window.FormEngine.events.list.VALIDATE, validationDto, this.identifier);
      let flaten = [].concat.apply([], results);
      /*
      console.log(
        "Validate function in Table results => ",this.identifier,
        flaten.filter((i: any) => !i.isValid)
      );
      */
      window.FormEngine.form.validation.resolveValidationResults(flaten, fillErrorBag, fillControlErrors);
      return flaten;
    },

    getFormattedCellValue(row: ITableRow, col: IColumnConfig): String {
      //console.log(row, col);
      let formatedValue = "NaN";
      if (col.prop) {
        let valFormFunc = this.config.displayValueFormatting.get(col.prop);
        if (valFormFunc) {
          formatedValue = valFormFunc(row, col.prop);
        } else {
          formatedValue = row.values.get(col.prop);
        }
      }
      return formatedValue;
    },
    // CREATE OPEN DETAIL VIEW
    openDetailView() {
      this.resetDetailView();
      window.FormEngine.events.publish(
        window.FormEngine.events.list.TABLE_DETAIL_OPEN,
        {
          validableId: this.identifier,
          control: this,
          action: "create",
        },
        this.identifier
      );
      this.state.detailViewOpened = true;
    },
    openDetailViewSelected() {
      this.resetDetailView();
      let row = this.state.rows.find((item) => item.checked);
      if (!row) {
        console.warn("Trying to open nonexistent row for editing!");
        return;
      }
      this.state.detailViewOpened = true;
      this.setCurrentRow(row.rowNumber, true);
      window.FormEngine.events.publish(
        window.FormEngine.events.list.TABLE_DETAIL_OPEN,
        {
          validableId: this.identifier,
          control: this,
          action: "edit",
        },
        this.identifier
      );
      this.validateDetailControls();
    },
    // EDIT OPEN DETAIL VIEW BY ROW GUID
    openDetailViewRowNumber(rowNumber: string) {
      this.unselectAll();
      this.closeDetailView();
      let row = this.state.rows.find((item) => item.rowNumber == parseInt(rowNumber));
      if (row) {
        row.checked = true;
        this.openDetailViewSelected();
      } else {
        console.warn(this.identifier + " => Table is trying to open for edit nonexistent row with number:", rowNumber, " from rows: ", this.state.rows);
      }
    },
    unselectAll() {
      this.state.rows.forEach((row) => (row.checked = false));
    },
    validateDetailControls(fillErrorBag: boolean = true) {
      let validationResults: Array<IValidatorResult> = [];
      if (this.config.detail && this.config.detail.Tree) {
        window.FormEngine.form.walkTreeWith(this.config.detail.Tree, function (control: any) {
          if (!control.state.hidden) {
            //let validationDto = window.FormEngine.form.validation.newValidatorDto({}, fillErrorBag, control);
            let results = control.validate(true);
            //let flatenResults = [].concat.apply([], results);
            //window.FormEngine.form.resolveValidationResults(flatenResults, validationDto);
            validationResults = [...validationResults, ...results];
          }
        });
      }
      return validationResults;
    },
    fillDetailControls(row: ITableRow, silent: boolean = false) {
      let self = this;
      //console.log("Fill detail control", row.rowNumber, silent);
      if (this.config.detail && this.config.detail.Tree) {
        window.FormEngine.form.walkTreeWith(
          this.config.detail.Tree,
          function (control: any) {
            control.config.editedParentRowNumber = row.rowNumber;

            //prerobit na zmenu stavu
            let mapping = Object.keys(self.config.valuesToControlsMapping).find((key) => key === control.identifier);
            let rowVal = row.rawValues as { [key: string]: any };
            if (mapping) {
              let value = rowVal[mapping];
              //console.log("setValue",control.identifier,value);
              //control.state.silentValueChangeEvent = true;
              control.setValue(value, silent);
              window.FormEngine.events.publish(
                window.FormEngine.events.list.VALUE_CHANGE,
                {
                  control: control,
                  from: control.state.oldValue,
                  to: value,
                },
                control.identifier
              );
            } else {
              console.warn("No mapping in table " + self.identifier + " for control " + control.identifier);
            }
          },
          function (section: any) {
            section.touch(true);
          }
        );
      }
    },
    closeDetailView() {
      this.state.detailViewOpened = false;
      //this.resetDetailView();
      window.FormEngine.events.publish(
        window.FormEngine.events.list.TABLE_EDIT_CLOSE,
        {
          validableId: this.identifier,
          control: this,
        },
        this.identifier
      );
    },
    resetDetailView() {
      this.state.editedRowNumber = 0;
      this.state.editedRow = null;
      if (this.config.detail && this.config.detail.Tree) {
        window.FormEngine.form.walkTreeWith(this.config.detail.Tree, function (element: any) {
          //console.log("ResetDetailView Reset =>", element.identifier);

          //  element.state.silentValueChangeEvent = true;
          element.reset(true);
          element.resetErrors();
          window.FormEngine.events.publish(
            window.FormEngine.events.list.VALUE_CHANGE,
            {
              control: element,
              from: element.state.oldValue,
              to: element.state.value,
            },
            element.identifier
          );
          element.config.editedParentRowNumber = null;
        }, function (element: any) {
          element.reset(true, false);
        });
      }
    },
    reset(silent: boolean = true) {
      //console.log("Reset Table => ", this.identifier);
      this.setValue(this.defaultState.value ? this.defaultState.value : [], silent);
      this.config.editedParentRowNumber = null;
      // this.state.silentSetValue = false;
      //this.resetErrors();
    },
    resetErrors() {
      this.state.errorMessages = [];
      this.state.isValid = true;
      //window.FormEngine.form.errorBag.removeErrors(this.identifier);
    },
    saveDetailView() {
      let table = this;
      let row = this.createRow(this.state.rows.length + 1);
      let rowIndex = this.state.rows.length;
      if (this.state.editedRowNumber !== 0) {
        rowIndex = (this.state.editedRowNumber as number) - 1;
        row = this.state.rows.find((item) => item.rowNumber === this.state.editedRowNumber) as ITableRow;
      }
      //let rawValues = {} as any;
      if (this.config.detail && this.config.detail.Tree) {
        window.FormEngine.form.walkTreeWith(this.config.detail.Tree, function (element: any) {
          if (!element.config.editedParentRowNumber) {
            element.config.editedParentRowNumber = row.rowNumber;
          }
          if (element.config.connectedColumn) {
            //console.log("Connected column config:", element.config.connectedColumn);
            row.values.set(element.config.connectedColumn, element.getValue());
          }
          let mapping = Object.keys(table.config.valuesToControlsMapping).find(
            (key) => table.config.valuesToControlsMapping[key] === element.identifier
          );
          //console.log("Column mapping:", mapping);
          row.rawValues[mapping ? mapping : element.identifier] = element.mtdt.IsTable ? element.getValue(false) : element.getValue();
        });
      }
      //row.rawValues = rawValues;
      if (this.state.editedRowNumber === 0) {
        this.state.rows.push(row);
      }
      this.state.silentSetValue = false;

      //this.validate();
      this.closeDetailView();
      //console.log("before resetDetailView ", this.identifier);
      this.resetDetailView();
      this.state.value[rowIndex] = row.rawValues;

      //console.log("after resetDetailView ", this.identifier);
      //this.validate(true,false);

      window.FormEngine.events.publish(
        window.FormEngine.events.list.TABLE_DETAIL_SAVE,
        {
          validableId: this.identifier,
          control: this,
          row: row,
          action: this.state.editedRowNumber === 0 ? "create" : "edit",
        },
        this.identifier
      );
    },
    removeSelectedRows() {
      let rows = [];
      let value = [];
      for (let rowIndex in this.state.rows) {
        //console.log(rowIndex);
        let row = this.state.rows[rowIndex];
        if (!row.checked) {
          rows.push(row);
          value.push(this.state.value[rowIndex]);
        }
      }

      this.state.value = value;
      this.state.rows = rows;
      this.refreshRowsOrder();
      this.validate();

      window.FormEngine.events.publish(
        window.FormEngine.events.list.TABLE_ROW_REMOVE,
        {
          validableId: this.identifier,
          control: this,
          action: "remove",
        },
        this.identifier
      );
    },
    orderByColumn(columnName: string) {
      this.state.value.sort(function (a: any, b: any) {
        return a[columnName] > b[columnName];
      });
    },
    removeError(validatorResult: IValidatorResult) {
      let errorIndex = this.state.errorMessages.findIndex(function (item) {
        return item.validatorGuid === validatorResult.validatorGuid && item.navigationData == validatorResult.navigationData && item.validatorGuid == validatorResult.validatorGuid;;
      });
      if (errorIndex !== -1) {
        this.state.errorMessages.splice(errorIndex, 1);
      }
      if (this.state.errorMessages.length === 0) {
        this.state.isValid = true;
      }
    },
    /**
     * Sets value and with it rows with standerd format.
     * @param val any|Array<any> - any value
     * @param redrawRows Boolean - if rows should be redrawn from new values with redrawRows function
     */
    setValue(val: any, silent: boolean = false, redrawRows = true) {
      this.state.silentSetValue = silent;
      this.state.value = val ? JSON.parse(JSON.stringify(val)) : [];
      if (redrawRows) this.redrawRows();
    },
    handleXmlImport(xml: Element, silent: boolean = true) {
      let self = this;
      //console.log(this.config.xmlNodeSelector, xml);
      let tempRows = xml.querySelectorAll(this.config.xmlNodeSelector as string);
      let tempRowsArray = Array.from(tempRows);
      let eventObject = {
        control: this,
        silent: silent,
        xmlRows: tempRowsArray,
        tableRows: this.state.rows
      }
      window.FormEngine.events.publish(
        window.FormEngine.events.list.BEFORE_IMPORT,
        eventObject,
        this.identifier
      );
      if (!XmlHelper.checkXmlElementsIfEmpty(eventObject.xmlRows)) {
        eventObject.xmlRows.forEach(function (item, index) {
          //console.log(item);
          self.handleXmlElementToRow(item, silent);
        });
      }
      window.FormEngine.events.publish(
        window.FormEngine.events.list.AFTER_IMPORT,
        eventObject,
        this.identifier
      );
    },
    handleXmlElementToRow(xmlRow: Element, silent: boolean = true) {
      let self = this;
      let row = this.createRow(this.state.value.length + 1);
      let rowIndex = row.rowNumber - 1;
      //let rawValues = {} as any;

      if (this.config.detail && this.config.detail.Tree) {
        window.FormEngine.form.walkTreeWith(this.config.detail.Tree, function (control: any) {
          if (!control.config.xmlNodeSelector && !control.config.xmlNodeCodeSelector) {
            return;
          }
          control.handleXmlImport(xmlRow, silent);
          if (control.config.connectedColumn) {
            //console.log("Connected column config:", element.config.connectedColumn);
            row.values.set(control.config.connectedColumn, control.state.value);
          }

          let mapping = Object.keys(self.config.valuesToControlsMapping).find(
            (key) => self.config.valuesToControlsMapping[key] === control.identifier
          );
          //console.log("Column mapping:", mapping);
          row.rawValues[mapping ? mapping : control.identifier] = control.state.value;
        });
      } else {
        console.log("THIS NEEDS TO BE TESTED PROPERLY!", this, this.config.valuesToColumnsMapping);
        for (let cont in this.config.valuesToColumnsMapping) {
          let column = this.config.columns.find((col) => col.prop === this.config.valuesToColumnsMapping[cont]);
          if (column) {
            let val = xmlRow.querySelector(column.xpathElementSelector as string);
            if (val) {
              if (column.prop !== cont) {
                row.rawValues[cont as string] = val.textContent;
              } else {
                row.rawValues[column.prop as string] = val.textContent;
              }
            }
          }
        }
      }
      this.state.silentSetValue = silent;
      this.state.value[rowIndex] = row.rawValues;
      //row.rawValues = this.state.value[rowIndex];
      this.state.rows.push(row);
      this.resetDetailView();
    },
    handleXmlExport(parentElement: Element, topLevelXml: Element) {
      let tableRows = this.state.rows;
      // RELATIVE PATHING FOR TABLE IN TABLE
      let eventObject = {
        control: this,
        tableRows: tableRows,
        parentElement: parentElement,
        topLevelXml: topLevelXml
      };
      window.FormEngine.events.publish(
        window.FormEngine.events.list.BEFORE_EXPORT,
        eventObject,
        this.identifier
      );
      let selectorPath = "";
      if (this.mtdt.IsTableDetailElement) {
        selectorPath =
          this.state.parentTableControl.config.xmlLastNodeSelector +
          (this.config.xmlNodeSelector as string).replace(
            this.state.parentTableControl.config?.xmlNodeSelector ? this.state.parentTableControl.config?.xmlNodeSelector : "",
            ""
          );
      } else {
        selectorPath = this.config.xmlNodeSelector as string;
      }
      if (eventObject.tableRows.length > 0 && selectorPath) {
        let controlXmlElement = parentElement.querySelector(selectorPath as string);
        for (let row of eventObject.tableRows) {
          this.setCurrentRow(row.rowNumber, true);
          if (controlXmlElement) {
            let cloneElement = controlXmlElement.cloneNode(true) as Element;
            this.handleRowToXmlElement(cloneElement, topLevelXml);
            if (controlXmlElement.previousElementSibling) {
              controlXmlElement.previousElementSibling.after(cloneElement);
            } else {
              let nodes = selectorPath.split(" > ");
              nodes.pop();
              // console.log(parentElement, nodes, cloneElement);

              if (nodes) {
                let lastElement = parentElement.querySelector(nodes.join(" > "));
                if (lastElement) {
                  // console.log("last ELEMENT!");
                  //console.log("lastElement found!",parentElement,cloneElement,lastElement);
                  controlXmlElement.before(cloneElement);
                } else {
                  // console.log("NOPE last ELEMENT!");
                  //console.log("lastElement not found",parentElement,cloneElement);
                  controlXmlElement.after(cloneElement);
                }
                //console.log("last element", lastElement);
              } else {
                //console.log("no nodes were found",parentElement,cloneElement)
                controlXmlElement.after(cloneElement);
              }
            }
          } else {
            console.log("No xml element for control ", selectorPath, this);
          }
        }
        controlXmlElement?.remove();
      }
      window.FormEngine.events.publish(
        window.FormEngine.events.list.AFTER_EXPORT,
        eventObject,
        this.identifier
      );
    },
    handleRowToXmlElement(parentElement: Element, topLevelXml: Element) {
      let table = this;
      if (this.config.detail && this.config.detail.Tree) {
        // console.log("Table with detail => PROCESSING START");
        window.FormEngine.form.walkTreeWith(this.config.detail.Tree, function (control: any) {
          control.handleXmlExport(parentElement, topLevelXml);
        });
      } else {
        console.log("Table without detail => PROCESSING START");

        for (let colKey in table.config.columns) {
          let col = table.config.columns[colKey];
          let lastPart = col.xpathElementSelector.split(" > ").splice(-1)[0];
          let valueKey = Object.keys(table.config.valuesToColumnsMapping).find((key) => table.config.valuesToColumnsMapping[key] === col.prop);
          if (valueKey && this.state.editedRow) {
            let val = this.state.editedRow.rawValues.get(valueKey);
            if (Array.isArray(val)) {
              console.log("Vnorena tabulka v tabulke ktora nema valuesToControlsMapping a ma v sebe dalsiu tabulku ", table);
            } else {
              if (lastPart) {
                XmlHelper.setElementValue(parentElement, lastPart, val);
              } else {
                console.log("Table mapping 2", col, valueKey);
              }
            }
          } else {
            console.log(col.xpathElementSelector, " doesn't have mapping");
          }
        }
      }
    },
    getSelectedValue(): any {
      let returnData: Map<String, any>[] = [];
      for (let index in this.state.rows) {
        if (this.state.rows[index].checked) {
          returnData[index] = this.state.rows[index].values;
        }
      }
      return returnData;
    },
    /**
     * Default funkcie z BaseControl
     */
    getValue(checked: boolean = true): any {
      let returnData: { [key: string]: any }[] = [];
      for (let index in this.state.rows) {
        if (checked) {
          if (this.state.rows[index].checked) {
            returnData.push(this.state.rows[index].rawValues);
            //returnData.push(this.state.rows[index].rawValues);
          }
        } else {
          returnData.push(this.state.rows[index].rawValues);
          //returnData.push(this.state.rows[index].rawValues);
        }
      }

      return returnData;
    },
    hide() {
      this.state.hidden = true;
    },
    show() {
      this.state.hidden = false;
    },
    inputChanged(val: any) {
      this.setValue(val);
    },
    setToExport(toExport: boolean) {
      this.state.toExport = toExport;
    },

    setErrors(errors: [IValidationErrorDto]) {
      if (errors.length > 0) {
        this.state.isValid = false;
      }
      this.state.errorMessages = errors;
    },
    pushError(error: IValidationErrorDto) {
      let tempError = this.state.errorMessages.findIndex((validationErrorDto) => {
        return (
          validationErrorDto.targetId == error.targetId &&
          validationErrorDto.validatorGuid == error.validatorGuid &&
          validationErrorDto.navigationData == error.navigationData
        );
      });
      //console.log(tempError,error);
      if (tempError === -1) {
        this.state.errorMessages.push(error);
      } else {
        this.state.errorMessages.splice(tempError, 1);
        this.state.errorMessages.push(error);
      }
      this.setValid(false);
    },
    setValid(valid: boolean = true) {
      this.state.isValid = valid;
    },
    getNavigationData(): [INavigationStep] {
      // TODO: Optimize and cache navigation data for control + possible rerender of navigation data (ak by sa pohybovali controlky odpajali a pripajali inde).
      if (!this.state.parentTableControl) {
        if (!this.state.parent) {
          return [
            {
              identifier: this.identifier,
              rowNumber: this.config.editedParentRowNumber,
              openedRow: this.state.editedRow?.rowNumber ? this.state.editedRow?.rowNumber : null,
            } as INavigationStep,
          ];
        }
        let navigationData = this.state.parent.getNavigationData();
        navigationData.push({
          identifier: this.identifier,
          rowNumber: this.config.editedParentRowNumber,
          openedRow: this.state.editedRow?.rowNumber ? this.state.editedRow?.rowNumber : null,
        } as INavigationStep);
        //console.log(navigationData);
        return JSON.parse(JSON.stringify(navigationData));
      } else {
        let navigationData = this.state.parentTableControl.getNavigationData();
        navigationData.push({
          identifier: this.identifier,
          rowNumber: this.config.editedParentRowNumber,
          openedRow: this.state.editedRow?.rowNumber ? this.state.editedRow?.rowNumber : null,
        } as INavigationStep);
        //console.log(navigationData);
        return JSON.parse(JSON.stringify(navigationData));
      }
    },
    /**
     * Row handling
     */
    getRowsValue(checked: boolean = true): any {
      let returnData: Map<String, any>[] = [];
      for (let index in this.state.rows) {
        if (checked) {
          if (this.state.rows[index].checked) {
            returnData[index] = this.state.rows[index].values;
          }
        } else {
          returnData[index] = this.state.rows[index].values;
        }
      }
      return returnData;
    },
    createRow(rowNumber: number): ITableRow {
      let row = {
        checked: false,
        values: new Map(),
        rawValues: {},
        valid: true,
        rowNumber: rowNumber,
        //guid: generateGuid(),
      } as ITableRow;
      if (this.config.counterControlIdentifier) {
        let columnName = this.config.columns[0].prop as string;
        row.values.set(columnName, rowNumber);
        row.rawValues[this.config.counterControlIdentifier] = rowNumber;
        //console.log(row, this.config.counterControlIdentifier);
      }
      return row;
    },
    hasErrorsRow(rowNumber: number) {
      return this.state.rows[rowNumber - 1].valid;
    },
    addRow(row: ITableRow) {
      this.state.rows.push(row);
    },
    createAndAddRow(values: Map<string, any>) {
      let row = this.createRow(this.state.rows.length + 1);
      row.values = values;
      this.addRow(row);
    },
    setRows(rows: Array<ITableRow>) {
      this.resetRows();
      this.state.rows = rows;
    },
    setRequired(required: boolean = true) {
      this.state.required = required as boolean;
    },
    setCurrentRow(rowNumber: number, silent: boolean = false) {
      let row = this.state.rows.find((item) => item.rowNumber === rowNumber) as ITableRow;
      window.FormEngine.events.publish(
        window.FormEngine.events.list.TABLE_ROW_BEFORE_CHANGE,
        {
          control: this,
          from: this.state.editedRow,
          to: row,
        },
        this.identifier
      );
      this.state.editedRow = row;
      this.state.editedRowNumber = row.rowNumber;
      this.fillDetailControls(row, silent);
      window.FormEngine.events.publish(
        window.FormEngine.events.list.TABLE_ROW_AFTER_CHANGE,
        {
          control: this,
          to: this.state.editedRow,
        },
        this.identifier
      );
    },
    resetRows() {
      this.state.rows = new Array<ITableRow>();
    },
    refreshRowsOrder() {
      let index = 0;
      this.state.rows.forEach((row) => {
        row.rowNumber = index + 1;
        index++;

        // TODO: Check if has order control if yes set order number there also
      });
    },
    /**
     * USE WITH CARE RESETS ROW GUID !!!
     * ONLY FOR reseting from state.value after which is revalidation recommended! (validation error navigation is based on row guid) ;
     */
    redrawRows() {
      this.resetRows();
      for (let valIndex in this.state.value) {
        let row = this.createRow(this.state.rows.length + 1);
        if (Object.keys(this.config.valuesToColumnsMapping).length > 0) {
          for (let attrName in this.config.valuesToColumnsMapping) {
            if (attrName !== "" && this.state.value[valIndex][attrName]) {
              row.values.set(this.config.valuesToColumnsMapping[attrName], this.state.value[valIndex][attrName]);
            }
          }
        }
        row.rawValues = this.state.value[valIndex];
        this.addRow(row);
      }
      //console.log("RedrawRows breaking VALIDATION => ", this.identifier, this.state.rows);
    },
    mapDetailControlsRecursive() {
      let self = this;
      if (this.config.detail && this.config.detail.Tree) {
        window.FormEngine.form.walkTreeWith(this.config.detail.Tree, function (element: any) {
          if (!element.Tree) {
            let column = self.config.columns.find((col: IColumnConfig) => {
              return col.xpath !== "" && element.mtdt.XpathEdoc !== "" && col.xpath === element.mtdt.XpathEdoc;
            });
            if (column && column.prop) {
              element.config.connectedColumn = column.prop;
              column.control = element.identifier;
              //console.log("HAS a column",element,column);
              self.config.valuesToControlsMapping[element.identifier as string] = column.prop as String;
            } else {
              if (element.config.rowCounter) {
                let column = self.config.columns[0];
                element.config.connectedColumn = column.prop;
                column.control = element.identifier;
              }
              //console.log("DOESN'T have a column connection in Xpath",element);
              self.config.valuesToControlsMapping[element.identifier as string] = element.identifier as String;
            }
          }
        });
      }
    },
  },
});
